import { useRef, useState } from 'react';
import type {
  DecoratedFileCollection,
  Directory,
  FileType,
} from '@witmetrics/utils';
import { useActivePractice, useDispatch } from '@witmetrics/data-store';
import { isZip } from '@witmetrics/utils/files';
import { mapToId, mapToKey } from '@witmetrics/utils/arrays';
import { isEmpty } from '@witmetrics/utils/misc';
import { currentDate, formatDateWithTime } from '@witmetrics/utils/time';
import { FILE_TYPES } from '@witmetrics/utils/constants';
import { useToggle } from '@/hooks/useToggle';
import { useAppState } from '@/providers/AppStateProvider';
import { uploadDirectoryTree } from '@/utils/files';
import { useLoadedFiles } from '@/providers/LoadedFilesProvider';
import { showErrorToaster } from '@/utils/toasters';
import FabricProvider from '@/providers/FabricProvider';
import DrawingCanvasProvider from '@/providers/DrawingCanvasProvider';
import { useArray } from '@/hooks/useArray';
import IconButton from '@/components/IconButton';
import { AddIcon } from '@/icons';
import NewFileMenu from '@/menus/NewFileMenu';
import FileCollectionDialog from '@/dialogs/FileCollectionDialog';
import UploadStagingDialog from '@/dialogs/UploadStagingDialog';
import UploadToaster from '@/toasters/UploadToaster';
import SmileSimulationOverlay from '@/overlays/SmileSimulationOverlay';
import { classes, uploadFiles } from './utils';

export type NewFileMenuButtonProps = {
  variant?: 'default' | 'aurora';
  parentFileCollectionId?: number;
  onImportSequence?: (fileId: number) => void;
  onUploadFiles?: (fileIds: number[]) => void;
  onUploadFileCollections?: (fileCollectionIds: number[]) => void;
  onNewFileCollection?: (fileCollectionId: number) => void;
};

export default function NewFileMenuButton({
  variant,
  parentFileCollectionId,
  onImportSequence,
  onUploadFiles,
  onUploadFileCollections,
  onNewFileCollection,
}: NewFileMenuButtonProps) {
  const menuToggleRef = useRef(null);
  const dispatch = useDispatch();
  const { onApiError, onToggleUnloadWarning } = useAppState();
  const activePractice = useActivePractice();
  const { addLoadedFiles } = useLoadedFiles();
  const [activeFile, setActiveFile] = useState<File | null>(null);
  const [uploads, setUploads, { clear: clearUploads }] = useArray();
  const [isMenuOpen, toggleMenu] = useToggle();
  const [isUploadToasterOpen, toggleUploadToaster] = useToggle();
  const [isCollectionDialogOpen, toggleCollectionDialog] = useToggle();
  const [isSmileOverlayOpen, toggleSmileOverlay] = useToggle();
  const [isStagingDialogOpen, toggleStagingDialog] = useToggle();

  const handleFileUpload = async (
    files: File[],
    onSuccess?: (fileIds: number[]) => void,
    fileTypeOverride?: FileType
  ): Promise<any> => {
    // TODO: Need to handle detecting file type (UPPER, LOWER, etc)
    try {
      if (!parentFileCollectionId) {
        return showErrorToaster('Error uploading file(s)');
      }
      setUploads(mapToKey(files, 'name'));
      toggleUploadToaster(true);
      onToggleUnloadWarning(true);
      const data = await uploadFiles(
        activePractice!.id,
        parentFileCollectionId,
        files,
        fileTypeOverride
      );
      onToggleUnloadWarning(false);
      addLoadedFiles(data.loadedFiles);
      dispatch.addFiles(data.files);
      dispatch.addFileCollectionFiles(data.fileCollectionFiles);
      dispatch.addFileVersions(data.fileVersions);
      toggleUploadToaster(false);
      clearUploads();
      onSuccess?.(mapToId(data.files));
    } catch (err) {
      toggleUploadToaster(false);
      clearUploads();
      onApiError(err, 'Error uploading file(s)');
    }
  };

  const handleImportSequence = async (files: File[]) => {
    // NOTE: Only accepts one zip file
    toggleMenu(false);
    if (isEmpty(files)) return;
    if (!isZip(files[0].name)) return showErrorToaster('Invalid sequence file');
    return handleFileUpload(
      [files[0]],
      (results) => onImportSequence?.(results[0]),
      FILE_TYPES.SWEETH_SEQUENCE.name // Override recognized file type
    );
  };

  const handleSelectFiles = async (files: File[]) => {
    toggleMenu(false);
    if (isEmpty(files)) return;
    return handleFileUpload(files, onUploadFiles);
  };

  const handleOpenUpload = () => {
    toggleMenu(false);
    toggleStagingDialog(true);
  };

  const handleUploadDirectory = (tree: Directory) => {
    return uploadDirectoryTree(
      tree,
      activePractice!.id,
      parentFileCollectionId,
      {
        onFileUploaded: (
          { file, fileCollectionFile, fileVersion },
          isTopLevel
        ) => {
          dispatch.addFiles([file]);
          dispatch.addFileCollectionFiles([fileCollectionFile]);
          dispatch.addFileVersions([fileVersion]);
          if (isTopLevel) onUploadFiles?.([file.id]);
        },
        onCollectionCreated: (fileCollection, isTopLevel) => {
          dispatch.addFileCollections([fileCollection]);
          if (isTopLevel) onUploadFileCollections?.([fileCollection.id]);
        },
        onError: (err) => onApiError(err, 'Error uploading directory'),
      }
    );
  };

  const handleStartNewFileCollection = () => {
    toggleMenu(false);
    toggleCollectionDialog(true);
  };

  const handleSaveFileCollection = (
    fileCollection: DecoratedFileCollection
  ) => {
    toggleCollectionDialog(false);
    onNewFileCollection?.(fileCollection.id);
  };

  const handleStartSmileSimulation = async (file: File) => {
    toggleMenu(false);
    setActiveFile(file);
    toggleSmileOverlay(true);
  };

  const handleSaveSimulation = async (result: File, compare: File) => {
    if (!activeFile) return;
    toggleSmileOverlay(false);
    const timestamp = formatDateWithTime(currentDate());
    await handleUploadDirectory({
      [`Smile Simulation - ${timestamp}`]: {
        [activeFile.name]: activeFile,
        ['result.png']: result,
        ['compare.png']: compare,
      },
    });
    setActiveFile(null);
  };

  return (
    <>
      <IconButton
        ref={menuToggleRef}
        className={variant === 'aurora' ? classes.auroraButton : undefined}
        onClick={() => toggleMenu()}>
        <AddIcon />
      </IconButton>
      {isMenuOpen && (
        <NewFileMenu
          anchorTo={menuToggleRef.current}
          open={isMenuOpen}
          disableAddFiles={!parentFileCollectionId}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          onStartSmileSimulation={handleStartSmileSimulation}
          onImportSequence={handleImportSequence}
          onAddFiles={handleSelectFiles}
          onAddFileCollection={handleStartNewFileCollection}
          onOpenUpload={handleOpenUpload}
          onClose={toggleMenu}
        />
      )}
      {isSmileOverlayOpen && activeFile && (
        <DrawingCanvasProvider>
          <FabricProvider>
            <SmileSimulationOverlay
              file={activeFile}
              onSave={handleSaveSimulation}
              onClose={() => toggleSmileOverlay(false)}
            />
          </FabricProvider>
        </DrawingCanvasProvider>
      )}
      {isCollectionDialogOpen && (
        <FileCollectionDialog
          parentFileCollectionId={parentFileCollectionId}
          onSave={handleSaveFileCollection}
          onClose={() => toggleCollectionDialog(false)}
        />
      )}
      {isStagingDialogOpen && (
        <UploadStagingDialog
          parentFileCollectionId={parentFileCollectionId}
          onUploadFileCollections={onUploadFileCollections}
          onUploadFiles={onUploadFiles}
          onClose={() => toggleStagingDialog(false)}
        />
      )}
      <UploadToaster
        open={isUploadToasterOpen}
        files={uploads}
        onClose={toggleUploadToaster}
      />
    </>
  );
}
